import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultHistoryProgress } from '@/types/history/history-progress';
import type {
    IHistorySocialFormValues,
    IHistorySocialModel,
    IHistorySocialSubmitResult,
} from '@/types/history/history-social';

const HISTORY_SOCIAL_ENDPOINT = '/api/register/history-social';

const defaultHistorySocialModel: IHistorySocialModel = {
    ...defaultHistoryProgress,
    currentRegistrationStep: DashRegistrationStep.HistorySocial,

    askSexualSocialQuestions: false,
    isMale: false,
    hasUsedTobacco: null,

    tobaccoYears: null,
    tobaccoPacks: null,
    tobaccoFrequency: null,

    hasUsedAlcohol: null,
    alcoholDrinks: null,
    alcoholFrequency: null,
    usesAlcohol: null,
    hasAbusedAlcohol: null,

    hasUsedDrugs: null,
    drugUseHistory: [],

    hasHadLegalIssues: null,
    hasBeenSentencedToPrison: null,
    prisonWhenAndWhere: '',
    hasTraveledLastYear: null,
    travelLocations: '',

    hasHadHighRiskSex: null,
    hasHadSexWithMmRelationsMale: null,
    hasHadMmSex: null,
    hasHadSexWithProstitute: null,
    hasProstituted: null,
    hasHadSexWithNeedleDrugUser: null,
    hasUsedNeedleDrugs: null,
    hasBeenInJailOver72: null,
    hasHadStd: null,
    hasBeenOnHemodialysis: null,
    hasHadSuicidalThoughtsAttempts: null,

    frequencyOptions: [],
    recreationalDrugOptions: [],
    yearsAgoOptions: [],
};

export const useHistorySocial = (registrationId: number, editKey: string) => {
    const url = `${HISTORY_SOCIAL_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel(url, defaultHistorySocialModel);

    const i = toIntOrNull;
    const b = toBoolOrNull;

    const { submit, isSubmitting, submitError } = useApiSubmit<IHistorySocialFormValues, DashRegistrationStep, IHistorySocialSubmitResult>(
        url,
        (values) => ({
            tobaccoYears: i(values.tobaccoYears),
            tobaccoPacks: i(values.tobaccoPacks),
            tobaccoFrequency: i(values.tobaccoFrequency),

            hasUsedAlcohol: b(values.hasUsedAlcohol),
            alcoholDrinks: i(values.alcoholDrinks),
            alcoholFrequency: i(values.alcoholFrequency),
            usesAlcohol: b(values.usesAlcohol),
            hasAbusedAlcohol: b(values.hasAbusedAlcohol),

            hasUsedDrugs: b(values.hasUsedDrugs),
            drugUseHistory: values.drugUseHistory.map((x) => ({
                recreationalDrug: i(x.recreationalDrug),
                yearsAgo: i(x.yearsAgo),
                hasAbusedDrug: b(x.hasAbusedDrug),
                hasReceivedTreatment: b(x.hasReceivedTreatment),
            })),

            hasHadLegalIssues: b(values.hasHadLegalIssues),
            hasBeenSentencedToPrison: b(values.hasBeenSentencedToPrison),
            prisonWhenAndWhere: values.prisonWhenAndWhere,
            hasTraveledLastYear: b(values.hasTraveledLastYear),
            travelLocations: values.travelLocations,

            hasHadHighRiskSex: b(values.hasHadHighRiskSex),
            hasHadSexWithMmRelationsMale: b(values.hasHadSexWithMmRelationsMale),
            hasHadMmSex: b(values.hasHadMmSex),
            hasHadSexWithProstitute: b(values.hasHadSexWithProstitute),
            hasProstituted: b(values.hasProstituted),
            hasHadSexWithNeedleDrugUser: b(values.hasHadSexWithNeedleDrugUser),
            hasUsedNeedleDrugs: b(values.hasUsedNeedleDrugs),
            hasBeenInJailOver72: b(values.hasBeenInJailOver72),
            hasHadStd: b(values.hasHadStd),
            hasBeenOnHemodialysis: b(values.hasBeenOnHemodialysis),
            hasHadSuicidalThoughtsAttempts: b(values.hasHadSuicidalThoughtsAttempts),
        }),
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
